// circle-pack.js
import * as d3 from 'd3';

class LawsCirclePack {
    constructor(container) {
        this.container = container;
        this.containerElement = document.querySelector(container);
        this.fontFamily = this.containerElement.dataset.font || 'inherit';
        // Color mapping - add your color names and hex values here
        this.colorMap = {
           'green': '#096849',
            'green-300': '#096849',
            'green-400': '#E7E8CE',
            'green-500': '#F2EED6',
            'pink': '#FF2C71',
            'pink-200': '#BA2051',
            'pink-500': '#FFE2D2',
            'amber-500': '#FFC375',
            'amber-200': '#D98600',
            // Add more colors as needed
        };

        this.darkTextColors = ['green-400', 'green-500', 'pink-500', 'amber-500'];

        
        // Get responsive dimensions
        this.updateDimensions();
        this.margin = { top: 0, right: 0, bottom: 0, left: 0 };
        
        // Read data from DOM
        this.data = this.readDataFromDOM();
        
        if (this.data.length > 0) {
            this.init();
            this.setupResize();
        }
    }
    

    getTextColor(colorString) {
        if (!colorString) return '#FFF6DD'; // Default to white
        
        // Check if this color needs dark text
        if (this.darkTextColors.includes(colorString.toLowerCase())) {
            return '#096849'; // Dark text color
        }
        
        return '#FFF6DD'; // Default white text
    }
    // Helper method to convert color string to hex
    getColorHex(colorString) {
        if (!colorString) return null;
        
        // If it's already a hex color, return it
        if (colorString.startsWith('#')) {
            return colorString;
        }
        
        // Convert to lowercase and lookup in map
        const hex = this.colorMap[colorString.toLowerCase()];
        return hex || null;
    }
    
    updateDimensions() {
        // Get container width or use viewport width
        const containerWidth = this.containerElement ? this.containerElement.offsetWidth : window.innerWidth;
        
        // Set width to container width (100% of container)
        this.width = containerWidth || 1000; // fallback to 800
        
        // Set height for landscape orientation
        // More landscape options:
        this.height = this.width * 1; // 2:1 ratio (wide landscape)
        // this.height = this.width * 0.5625; // 16:9 ratio (standard widescreen)
        // this.height = this.width * 0.6; // 5:3 ratio (moderate landscape)
        
        // Option: Min/max constraints
        this.height = Math.min(Math.max(this.height, 300), 600);
    }
    
    setupResize() {
        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.updateDimensions();
                this.resize();
            }, 250); // Debounce resize events
        });
    }
    
    resize() {
        // Update SVG dimensions
        this.svg
            .attr('width', this.width)
            .attr('height', this.height)
            .attr('viewBox', `0 0 ${this.width} ${this.height}`);
        
        // Clear and re-render
        this.g.selectAll('*').remove();
        this.render();
    }
    
    readDataFromDOM() {
        const dataItems = document.querySelectorAll('.law-data-item');
        const data = [];
        
        dataItems.forEach(item => {
            const template = item.querySelector('.law-modal-template');
            
            data.push({
                id: item.dataset.id,
                title: item.dataset.title,
                size: parseInt(item.dataset.size) || 10,
                color: item.dataset.color,
                template: template ? template.innerHTML : null
            });
        });
        
        return data;
    }
    
    init() {
        // Create SVG
        this.svg = d3.select(this.container)
            .append('svg')
            .attr('width', this.width)
            .attr('height', this.height)
            .attr('viewBox', `0 0 ${this.width} ${this.height}`)
            .style('max-width', '100%')
            .style('height', 'auto');
            
        this.g = this.svg.append('g')
            .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
            
        // Create color scale
        this.colorScale = d3.scaleOrdinal(d3.schemeCategory10);
        
        this.render();
        this.setupModal();
    }
    
    render() {
        // Transform data for D3 hierarchy
        const hierarchyData = {
            name: "Laws",
            children: this.data.map(d => ({
                name: d.title,
                size: d.size,
                data: d
            }))
        };
        
        // Create hierarchy and pack layout
        const root = d3.hierarchy(hierarchyData)
            .sum(d => d.size)
            .sort((a, b) => b.size - a.size);
            
        const pack = d3.pack()
            .size([this.width - this.margin.left - this.margin.right, 
                   this.height - this.margin.top - this.margin.bottom])
            .padding(5); // Increase this for more spacing between circles (default was 3)
            
        const nodes = pack(root).descendants().slice(1); // Remove root node
        
        // Add a large circle around all packed circles (using the root node)
        const rootNode = pack(root);
        this.g.append('circle')
            .attr('class', 'outer-circle')
            .attr('cx', rootNode.x)
            .attr('cy', rootNode.y)
            .attr('r', rootNode.r)
            .attr('fill', '#FFF6DD')
            .attr('stroke', '#FFF6DD')
            .attr('stroke-width', 0)
            .attr('opacity', 1);
        
        // Create circles
        const circles = this.g.selectAll('.law-circle')
            .data(nodes)
            .enter()
            .append('g')
            .attr('class', 'law-circle')
            .attr('transform', d => `translate(${d.x},${d.y})`)
            .style('cursor', 'pointer');
            
        // Add circle backgrounds
        circles.append('circle')
            .attr('r', d => d.r)
            .attr('fill', d => {
                // Use custom color if available, otherwise fallback to color scale
                const customColor = this.getColorHex(d.data.data.color);
                return customColor || this.colorScale(d.data.data.id);
            })
            .attr('stroke', '#fff')
            .attr('stroke-width', 0)
            .attr('opacity', 1)
            .on('mouseover', function(event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('opacity', 1)
                      .attr('stroke', '#2E2E2E')
                    .attr('stroke-width', 5);
            })
            .on('mouseout', function(event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('opacity', 1)
                    .attr('stroke-width', 0);
            })
            .on('click', (event, d) => {
                // Remove active state from all circles
                this.g.selectAll('.circle-element')
                    .classed('active', false)
                    .attr('stroke-width', 0);
                
                // Add active state to clicked circle
                d3.select(event.currentTarget)
                    .classed('active', true)
                    .attr('stroke', '#2E2E2E') // Or a different color for active
                    .attr('stroke-width', 5);
                
                this.openModal(d.data.data);
            });
            
        // Add text labels
        circles.append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '0.3em')
            .style('font-family', this.fontFamily) // Will inherit from container
            .style('font-size', d => Math.min(d.r / 1, 24) + 'px')
            .style('fill', d => this.getTextColor(d.data.data.color))  // <-- This line sets text color
            .style('pointer-events', 'none')
            .style('user-select', 'none')
            .each((d, i, nodes) => {
              
                
                // Only wrap text for circles large enough
                if (d.r > 30) {
                 
                    this.wrapText(d, nodes[i]);
                } else {
             
                    // For small circles, just add simple text without wrapping
                    d3.select(nodes[i]).text(d.data.name);
                }
            });
    }
    
    // Text wrapping function
    wrapText(d, textEl) {
        console.log('wrapText called for:', d.data.name);
        
        const text = d3.select(textEl);
        const words = d.data.name.split(/\s+/).reverse();
        const lineHeight = .8
        const maxWidth = d.r * 1.6;
        
        let word;
        let line = [];
        let lineNumber = 0;
        const lines = []; // Track all lines
        const x = text.attr('x') || 0;
        const y = text.attr('y') || 0;
        
        // Clear ALL content
        text.selectAll('*').remove();
        text.text('');
        
        let tspan = text.append('tspan')
            .attr('x', x)
            .attr('y', y);
        
        while (word = words.pop()) {
            line.push(word);
            tspan.text(line.join(' '));
            
            if (tspan.node().getComputedTextLength() > maxWidth && line.length > 1) {
                // Remove last word from current line
                line.pop();
                tspan.text(line.join(' '));
                lines.push(line.join(' '));
                
                // Start new line with the word that didn't fit
                line = [word];
                lineNumber++;
                tspan = text.append('tspan')
                    .attr('x', x)
                    .attr('y', y)
                    .text(word);
            }
        }
        // lines.push(line.join(' ')); // Add the last line
        
        // Now center the text vertically based on number of lines
        const totalLines = lines.length;
        const verticalOffset = -(totalLines - 1) * lineHeight * 0.5; // Center offset
        
        text.selectAll('tspan').each(function(d, i) {
            d3.select(this).attr('dy', (i * lineHeight + verticalOffset) + 'em');
        });
        
        console.log('Final tspan count:', text.selectAll('tspan').size());
    }
    
    setupModal() {
        const modal = document.getElementById('law-modal');
        const closeBtn = document.querySelector('.law-modal-close');
        const overlay = document.querySelector('.law-modal-overlay');
        
        // Close button handler
        if (closeBtn) {
            closeBtn.onclick = () => {
                modal.style.display = 'none';
                document.body.style.overflow = 'auto';
            };
        }
        
        // Overlay click handler
        if (overlay) {
            overlay.onclick = () => {
                modal.style.display = 'none';
                document.body.style.overflow = 'auto';
            };
        }
        
        // Escape key handler
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.style.display === 'block') {
                modal.style.display = 'none';
                document.body.style.overflow = 'auto';
            }
        });
    }
    
    openModal(lawData) {
        const modal = document.getElementById('law-modal');
        const modalBody = document.querySelector('.law-modal-body');
        const sidePanel = document.getElementById('side-panel-content');
        
        // Create content HTML
        // const titleEl = document.createElement('h2');
        // titleEl.className = 'modal-title';
        // titleEl.textContent = lawData.title;
        
        let contentHTML = ''; //titleEl.outerHTML;
        
        if (lawData.template) {
            contentHTML += lawData.template;
        } else {
            contentHTML += '<p>No content available.</p>';
        }
        
        // Populate side panel (desktop)
        if (sidePanel) {
            sidePanel.innerHTML = contentHTML;
        }
        
        // Populate modal (mobile)
        if (modalBody) {
            modalBody.innerHTML = contentHTML;
        }
        
        // Show modal on mobile only (< 1024px)
        if (modal && window.innerWidth < 1024) {
            modal.style.display = 'block';
            document.body.style.overflow = 'hidden';
            
            // Focus on close button for accessibility
            const closeBtn = document.querySelector('.law-modal-close');
            if (closeBtn) closeBtn.focus();
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    const container = '#laws-circle-pack';
    if (document.querySelector(container)) {
        new LawsCirclePack(container);
    }
});

// Export for npm usage
export default LawsCirclePack;